import {
  Box,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
  Chip,
  Divider,
} from "@mui/material";
import {
  Image as ImageIcon,
  Upload as UploadIcon,
  Close as CloseIcon,
  Language as GlobeIcon,
  PhotoLibrary as GalleryIcon,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useTranslationApp } from "../../../lib/i18next";
import UploadFileButton from "../../Buttons/UploadFileButton";
import Button from "../../Buttons/Button";
import { useDispatchApp } from "../../../lib/redux";
import { getPathDatabaseByRole } from "../../../actions/auth";
import {
  BUSINESS_OFFERING_SERVICES,
  FONT_SIZE_BUTTON,
  IMAGE_FORMATS,
} from "../../../utils/constants";
import {
  MODAL_CREATE_WITH_IA_UPLOAD_IMAGE,
  MODAL_CREATE_WITH_IA_GALLERY_BUTTON,
  MODAL_CREATE_WITH_IA_IMAGES_COUNT,
  MODAL_CREATE_WITH_IA_SERVICE_IMAGES_COUNT,
  MODAL_CREATE_WITH_IA_MAX_IMAGES_MESSAGE,
  MODAL_CREATE_WITH_IA_EXTRACT_URL,
  MODAL_CREATE_WITH_IA_IMPORT_PHOTOS_URL_HINT,
  CONFIGURATION_PANEL_REFERENCE_IMAGE_REQUIREMENTS_TITLE,
  CONFIGURATION_PANEL_WEBSITE_URL_TITLE,
  CONFIGURATION_PANEL_ADD_MULTIPLE_PERSPECTIVES_TIP,
  CONFIGURATION_PANEL_REFERENCE_IMAGE_AVOID_PEOPLE,
  MODAL_CREATE_WITH_IA_CONFIGURATION_REFERENCE_IMAGE_TITLE_PRODUCTS,
  MODAL_CREATE_WITH_IA_CONFIGURATION_REFERENCE_IMAGE_TITLE_SERVICES,
  MODAL_CREATE_WITH_IA_REFERENCE_IMAGE_ASSETS_CHIP,
  MODAL_CREATE_WITH_IA_CONFIGURATION_REFERENCE_IMAGE_DESCRIPTION_PRODUCTS_URL,
  MODAL_CREATE_WITH_IA_CONFIGURATION_REFERENCE_IMAGE_DESCRIPTION_PRODUCTS_UPLOAD,
  MODAL_CREATE_WITH_IA_CONFIGURATION_REFERENCE_IMAGE_DESCRIPTION_SERVICES_URL,
  MODAL_CREATE_WITH_IA_CONFIGURATION_REFERENCE_IMAGE_DESCRIPTION_SERVICES_UPLOAD,
  CONFIGURATION_PANEL_SERVICE_WEBSITE_URL_TITLE,
} from "../../../i18n/keysTranslations";
import TextField from "../../Form/TextField";
import TooltipModal from "../../Tooltip/TooltipModal";
import ContentReferenceImageRequirements from "../../Tooltip/ContentReferenceImageRequirements";
import {
  getCreateWithIAReferenceImageLimit,
  mergeReferenceImageUrls,
} from "../../../utils/createWithIAReferenceImages";

const ACTION_BUTTON_SX = {
  height: 36,
  fontSize: FONT_SIZE_BUTTON,
  width: "100%",
};

const FULL_WIDTH_ACTION_SX = {
  width: "100%",
  "& > div": {
    width: "100%",
    display: "flex",
  },
  "& .MuiButton-root": {
    width: "100%",
  },
};

const ASSETS_CHIP_SX = {
  bgcolor: "#E6F0F9",
  color: "#4A76A8",
  fontWeight: 600,
  fontSize: 10,
  height: 22,
  borderRadius: 999,
  "& .MuiChip-label": {
    px: 1,
    py: 0,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
};

const ReferenceImageThumb = ({ url, onRemove }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(false);
  }, [url]);

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        sx={{
          aspectRatio: "1",
          bgcolor: "grey.100",
          borderRadius: 1,
          border: 1,
          borderColor: "divider",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {!isLoaded && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "grey.100",
              zIndex: 1,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}
        <img
          src={url}
          alt=""
          onLoad={() => setIsLoaded(true)}
          onError={() => setIsLoaded(true)}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: isLoaded ? 1 : 0,
            transition: "opacity 0.2s ease-out",
          }}
        />
        <IconButton
          size="small"
          onClick={onRemove}
          sx={{
            position: "absolute",
            top: { xs: 2, sm: 4 },
            right: { xs: 2, sm: 4 },
            bgcolor: "black",
            width: { xs: 16, sm: 20 },
            height: { xs: 16, sm: 20 },
            zIndex: 2,
          }}
        >
          <CloseIcon
            sx={{
              fontSize: { xs: 10, sm: 12 },
              color: "white !important",
            }}
          />
        </IconButton>
      </Box>
    </Box>
  );
};

const ReferenceImageRequirementsTooltip = ({ showAvoidPeople = false }) => {
  const { t } = useTranslationApp();

  return (
    <TooltipModal
      sx={{
        px: 0.75,
        fontSize: "0.9rem",
        filter: "brightness(0.9)",
        flexShrink: 0,
      }}
      title={t(CONFIGURATION_PANEL_REFERENCE_IMAGE_REQUIREMENTS_TITLE)}
      content={
        <ContentReferenceImageRequirements showAvoidPeople={showAvoidPeople} />
      }
    />
  );
};

const SectionHeading = ({
  icon,
  title,
  chipLabel = "",
  showTooltip = false,
  showAvoidPeople = false,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      flexWrap: "wrap",
    }}
  >
    {icon}
    <Typography
      variant="subtitle2"
      sx={{ fontWeight: 600, color: "text.primary" }}
    >
      {title}
    </Typography>
    {chipLabel ? (
      <Chip label={chipLabel} size="small" sx={ASSETS_CHIP_SX} />
    ) : null}
    {showTooltip ? (
      <ReferenceImageRequirementsTooltip showAvoidPeople={showAvoidPeople} />
    ) : null}
  </Box>
);

const SectionDescription = ({ children }) => (
  <Typography
    variant="body2"
    sx={{
      color: "text.secondary",
      fontSize: 12,
      lineHeight: 1.6,
    }}
  >
    {children}
  </Typography>
);

const ReferenceImageSelector = ({
  control,
  errors,
  onExtractFromUrl,
  setValue,
  getValues,
  watch,
  onOpenGallery,
  businessOffering,
  mediaType,
}) => {
  const { t } = useTranslationApp();
  const [isUploading, setIsUploading] = useState(false);

  const isService = businessOffering === BUSINESS_OFFERING_SERVICES;

  const urlTitleKey = isService
    ? CONFIGURATION_PANEL_SERVICE_WEBSITE_URL_TITLE
    : CONFIGURATION_PANEL_WEBSITE_URL_TITLE;
  const urlDescriptionKey = isService
    ? MODAL_CREATE_WITH_IA_CONFIGURATION_REFERENCE_IMAGE_DESCRIPTION_SERVICES_URL
    : MODAL_CREATE_WITH_IA_CONFIGURATION_REFERENCE_IMAGE_DESCRIPTION_PRODUCTS_URL;
  const photosTitleKey = isService
    ? MODAL_CREATE_WITH_IA_CONFIGURATION_REFERENCE_IMAGE_TITLE_SERVICES
    : MODAL_CREATE_WITH_IA_CONFIGURATION_REFERENCE_IMAGE_TITLE_PRODUCTS;
  const photosDescriptionKey = isService
    ? MODAL_CREATE_WITH_IA_CONFIGURATION_REFERENCE_IMAGE_DESCRIPTION_SERVICES_UPLOAD
    : MODAL_CREATE_WITH_IA_CONFIGURATION_REFERENCE_IMAGE_DESCRIPTION_PRODUCTS_UPLOAD;

  const imagesUrl = watch("imagesUrl") || [];
  const isVideo = mediaType === "video";
  const websiteUrl = watch("websiteUrl") || "";
  const hasWebsiteUrl = websiteUrl.trim().length > 0;
  const maxImages = getCreateWithIAReferenceImageLimit({ mediaType });
  const isAtImageLimit = imagesUrl.length >= maxImages;

  const dispatch = useDispatchApp();
  const { userID } = dispatch(getPathDatabaseByRole());

  const onImageUpload = (newImages = []) => {
    const currentImages = getValues("imagesUrl") || [];
    setValue(
      "imagesUrl",
      mergeReferenceImageUrls({
        currentUrls: currentImages,
        newUrls: newImages,
        mediaType,
      })
    );
  };

  const onRemoveImage = (index) => {
    const currentUrls = getValues("imagesUrl") || [];
    setValue(
      "imagesUrl",
      currentUrls.filter((_, imageIndex) => imageIndex !== index)
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <SectionHeading
          icon={<GlobeIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
          title={t(urlTitleKey)}
        />
        <SectionDescription>{t(urlDescriptionKey)}</SectionDescription>
        <TextField
          control={control}
          name="websiteUrl"
          placeholder={`${t(urlTitleKey)}...`}
          fullWidth
          variant="filled"
          errors={errors}
          rules={{ required: false }}
        />
      </Box>

      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        <SectionHeading
          icon={<ImageIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
          title={t(photosTitleKey)}
          chipLabel={t(MODAL_CREATE_WITH_IA_REFERENCE_IMAGE_ASSETS_CHIP)}
          showTooltip
          showAvoidPeople={isVideo}
        />
        <SectionDescription>{t(photosDescriptionKey)}</SectionDescription>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={FULL_WIDTH_ACTION_SX}>
            <UploadFileButton
              id="upload-reference-images"
              textUpload={MODAL_CREATE_WITH_IA_UPLOAD_IMAGE}
              colorUpload="secondary"
              onChange={onImageUpload}
              pathStorage={`socialMedia/${userID}/`}
              acceptedFormats={IMAGE_FORMATS}
              loading={isUploading}
              onCallbackLoading={(loading) => setIsUploading(loading)}
              modeUpload="sync"
              multiple={true}
              icon={<UploadIcon sx={{ fontSize: "18px !important" }} />}
              disabled={isAtImageLimit}
              sx={ACTION_BUTTON_SX}
            />
          </Box>
          <Button
            onClick={onOpenGallery}
            color="secondary"
            size="small"
            fullWidth
            startIcon={<GalleryIcon sx={{ fontSize: 16 }} />}
            disabled={isAtImageLimit}
            sx={ACTION_BUTTON_SX}
          >
            {t(MODAL_CREATE_WITH_IA_GALLERY_BUTTON)}
          </Button>
          <Button
            onClick={onExtractFromUrl}
            color="secondary"
            size="small"
            fullWidth
            startIcon={<GlobeIcon sx={{ fontSize: 16 }} />}
            disabled={!hasWebsiteUrl || isAtImageLimit}
            sx={ACTION_BUTTON_SX}
          >
            {t(MODAL_CREATE_WITH_IA_EXTRACT_URL)}
          </Button>
          {!hasWebsiteUrl && !isAtImageLimit ? (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: { xs: 10, sm: 11 },
                display: "block",
              }}
            >
              {t(MODAL_CREATE_WITH_IA_IMPORT_PHOTOS_URL_HINT)}
            </Typography>
          ) : null}
          {isAtImageLimit ? (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: { xs: 10, sm: 11 },
                display: "block",
                textAlign: "center",
              }}
            >
              {t(MODAL_CREATE_WITH_IA_MAX_IMAGES_MESSAGE, { maxImages })}
            </Typography>
          ) : null}
        </Box>

        {imagesUrl.length > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ImageIcon sx={{ fontSize: 18, color: "text.secondary" }} />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                {t(
                  isService
                    ? MODAL_CREATE_WITH_IA_SERVICE_IMAGES_COUNT
                    : MODAL_CREATE_WITH_IA_IMAGES_COUNT,
                  {
                    count: imagesUrl.length,
                  }
                )}
              </Typography>
            </Box>
            <Grid container spacing={{ xs: 0.5, sm: 1 }}>
              {imagesUrl.map((url, index) => (
                <Grid item xs={4} sm={6} key={`${url}-${index}`}>
                  <ReferenceImageThumb
                    url={url}
                    onRemove={() => onRemoveImage(index)}
                  />
                </Grid>
              ))}
            </Grid>
            {isVideo ? (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontSize: 12,
                  lineHeight: 1.5,
                }}
              >
                {t(CONFIGURATION_PANEL_REFERENCE_IMAGE_AVOID_PEOPLE)}
              </Typography>
            ) : null}
          </Box>
        ) : null}

        {!isService ? (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: 11,
              fontStyle: "italic",
              lineHeight: 1.5,
            }}
          >
            {t(CONFIGURATION_PANEL_ADD_MULTIPLE_PERSPECTIVES_TIP)}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default ReferenceImageSelector;
